import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Wifi, WifiOff, Loader2, FilePlus, Wrench } from "lucide-react";
import { getCategoryColorsByLabel } from "@/lib/categoryColors";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useArtisanProfile, useUpdateAvailability } from "@/hooks/useArtisanProfile";
import { useEffect } from "react";
import ArtisanBottomNav from "@/components/ArtisanBottomNav";
import DashboardUpcomingAppointments from "@/components/DashboardUpcomingAppointments";
import DashboardNearbyAnnonces from "@/components/DashboardNearbyAnnonces";
import DashboardDevisPreview from "@/components/DashboardDevisPreview";
import ProfileCompletionCard from "@/components/ProfileCompletionCard";
import { ARTISAN_AVAILABILITY } from "@/lib/constants";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";

const DashboardArtisan = () => {
  useSEO({ noindex: true });
  const { t, locale } = useLanguage("artisan");
  const lNavigate = useLocalizedNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: profile } = useArtisanProfile();
  const updateAvailability = useUpdateAvailability();

  const availability = (profile as any)?.availability || "disponible";
  const isAvailable = availability === "disponible";

  useEffect(() => {
    if (!authLoading && !user) lNavigate("/connexion");
  }, [user, authLoading, lNavigate]);

  if (authLoading) return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    </div>
  );

  const hour = new Date().getHours();
  const greeting = hour < 18 ? t("dashboard.greeting_day") : t("dashboard.greeting_evening");
  const firstName = user?.user_metadata?.first_name;
  const lastName = user?.user_metadata?.last_name;
  const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || null;
  const displayName = (profile as any)?.raison_sociale || fullName || user?.email?.split("@")[0] || "artisan";

  return (
    <div className="min-h-screen pb-20 md:pb-0 bg-background">
      <Navbar />

      {/* Header — identité + disponibilité */}
      <div className="border-b bg-card/80 backdrop-blur-sm">
        <div className="container py-5 md:py-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{greeting},</p>
              <h1 className="font-display text-xl font-bold md:text-2xl capitalize">{displayName}</h1>
              {(profile as any)?.specialite && (() => {
                const c = getCategoryColorsByLabel((profile as any).specialite);
                return (
                  <span className={cn("mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border", c.badgeBg, c.badgeText)}>
                    <Wrench className="h-3 w-3" />
                    {(profile as any).specialite}
                  </span>
                );
              })()}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="hero"
                size="sm"
                className="gap-1.5"
                onClick={() => lNavigate("/devis")}
              >
                <FilePlus className="h-4 w-4" />
                {locale === "ar" ? "إنشاء عرض سعر" : "Créer un devis"}
              </Button>
              {(profile?.wilaya || profile?.commune) && (
                <div className="flex items-center gap-1.5 rounded-lg border bg-background px-3 py-2 text-sm">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
                  <span className="font-medium text-xs">
                    {profile?.commune ? `${profile.commune}, ` : ""}{profile?.wilaya || "—"}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2 rounded-lg border bg-background px-2 py-1.5">
                {isAvailable
                  ? <Wifi className="h-3.5 w-3.5 text-accent shrink-0" aria-hidden="true" />
                  : <WifiOff className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
                }
                <Select
                  value={availability}
                  onValueChange={(v) => updateAvailability.mutate(v)}
                >
                  <SelectTrigger
                    className="border-0 h-auto p-0 w-auto text-xs font-medium shadow-none focus:ring-0 bg-transparent"
                    aria-label={t("dashboard.availability_label")}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ARTISAN_AVAILABILITY.map((a) => (
                      <SelectItem key={a.value} value={a.value}>{locale === "ar" ? a.label_ar : a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="container py-6 space-y-8">
        {profile && <ProfileCompletionCard profile={profile} />}
        <DashboardUpcomingAppointments />
        <DashboardDevisPreview />
        <DashboardNearbyAnnonces />
      </main>

      <div className="hidden md:block"><Footer /></div>
      <ArtisanBottomNav />
    </div>
  );
};

export default DashboardArtisan;
